import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Clapperboard, Loader2, Mic2, Play, Sparkles, Upload, Volume2, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { getImageUrl } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const KLING_MAX_NATIVE_VOICES = 3; // one per Kling Element (identity) in a clip

const detailMsg = (err, fallback) => { const d = err.response?.data?.detail; return typeof d === 'string' ? (d.startsWith('MISSING_KEY:') ? d.split('|')[1] : d) : fallback; };

// Per-character voice profile — injected into every video prompt (all providers) so the voice stays consistent
export const VoiceDescriptionEditor = ({ char, projectId, onUpdated }) => {
  const [value, setValue] = useState(char.voice_description || '');
  const [editing, setEditing] = useState(!char.voice_description);
  const [busy, setBusy] = useState(null);
  const save = async (text) => {
    setBusy('save');
    try {
      const res = await api.put(`/projects/${projectId}/characters/${char.id}`, { voice_description: text.trim() });
      onUpdated?.(res.data); setValue(res.data.voice_description || ''); setEditing(!res.data.voice_description);
      toast.success(text.trim() ? 'Voice profile saved — used in every scene' : 'Voice profile cleared');
    } catch (err) { toast.error(detailMsg(err, 'Failed to save voice profile')); }
    finally { setBusy(null); }
  };
  const draft = async () => {
    setBusy('draft');
    try {
      const res = await api.post(`/projects/${projectId}/characters/${char.id}/generate-voice-description`);
      onUpdated?.(res.data); setValue(res.data.voice_description || ''); setEditing(false);
      toast.success('Voice profile drafted');
    } catch (err) { toast.error(detailMsg(err, 'Could not draft voice profile')); }
    finally { setBusy(null); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06]" data-testid={`voice-desc-block-${char.id}`}>
      <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#10B981] mb-2 flex items-center gap-1.5">
        <Mic2 className="w-3 h-3" /> Voice Profile
        <span className="text-[8px] px-1.5 py-px rounded-full bg-[#10B981]/10 border border-[#10B981]/25 tracking-normal normal-case font-medium" title="Injected into every video prompt this character speaks in — Kling, Veo and Seedance.">All providers</span>
      </label>
      {editing ? (
        <>
          <textarea data-testid={`voice-desc-input-${char.id}`} value={value} onChange={e => setValue(e.target.value)} maxLength={300} rows={3}
            placeholder="e.g. mid-40s male, low gravelly baritone, unhurried, light Glaswegian accent, dry and weary"
            className="ff-input w-full text-[11px] resize-none" />
          <div className="flex items-center gap-2 mt-1.5">
            <button data-testid={`voice-desc-save-${char.id}`} onClick={() => save(value)} disabled={busy || value.trim() === (char.voice_description || '')}
              className="text-[10px] px-2.5 py-1 rounded-lg bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 hover:bg-[#10B981]/25 disabled:opacity-40 flex items-center gap-1">
              {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save
            </button>
            <button data-testid={`voice-desc-draft-${char.id}`} onClick={draft} disabled={!!busy}
              className="text-[10px] px-2.5 py-1 rounded-lg bg-white/[0.04] text-[#C4B5FD] border border-white/10 hover:text-white disabled:opacity-40 flex items-center gap-1">
              {busy === 'draft' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Draft with AI
            </button>
            {char.voice_description && <button onClick={() => { setValue(char.voice_description); setEditing(false); }} className="text-[10px] text-[#8A8A93] hover:text-white">Cancel</button>}
          </div>
        </>
      ) : (
        <div className="flex items-start gap-2">
          <p data-testid={`voice-desc-text-${char.id}`} className="flex-1 text-[11px] text-[#c8c8d0] leading-relaxed">{char.voice_description}</p>
          <button data-testid={`voice-desc-edit-${char.id}`} onClick={() => setEditing(true)} className="text-[10px] text-[#FF4500] hover:text-[#FF5722] shrink-0">Edit</button>
        </div>
      )}
    </div>
  );
};

// Capture a character's voice from a generated take and lock it as their Kling native voice
export const CaptureVoiceModal = ({ projectId, sceneId, scene, videoPath, characters, onClose, onCaptured }) => {
  const sceneChars = characters.filter(c => (scene?.character_ids || []).includes(c.id));
  const [charId, setCharId] = useState(sceneChars[0]?.id || '');
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const stopAtRef = useRef(null);
  const length = Math.max(0, end - start);
  const valid = charId && length >= 5 && length <= 30;
  const onLoaded = () => { const d = videoRef.current?.duration || 0; if (!end) setEnd(Math.min(d, 30)); };
  const onTime = () => { if (stopAtRef.current != null && videoRef.current && videoRef.current.currentTime >= stopAtRef.current) { videoRef.current.pause(); stopAtRef.current = null; } };
  const previewSegment = () => { const v = videoRef.current; if (!v) return; v.currentTime = start; stopAtRef.current = end; v.play(); };
  const capture = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${sceneId}/capture-voice`, { character_id: charId, start, end, video_path: videoPath }, { timeout: 180000 });
      onCaptured?.(res.data.character);
      toast.success(`Locked ${res.data.character.name}'s native voice from this take (Kling)`);
      onClose();
    } catch (err) { toast.error(detailMsg(err, 'Voice capture failed')); }
    finally { setBusy(false); }
  };
  const r2 = (n) => Math.round(n * 10) / 10;
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px]" data-testid="capture-voice-modal" onClick={onClose}>
      <div data-testid="slide-over-panel" className="absolute right-0 top-0 h-full w-full max-w-[600px] bg-[#0E0E12] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right p-5 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-white flex items-center gap-2"><Clapperboard className="w-4 h-4 text-[#E879F9]" /> Capture voice from this take</p>
            <p className="text-[11px] text-[#8A8A93] mt-0.5">Mark a 5–30s range where this character speaks alone. Kling clones it and locks it as their native voice for future renders.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]"><X className="w-5 h-5" /></button>
        </div>
        <div className="rounded-lg overflow-hidden bg-black"><video ref={videoRef} controls className="w-full max-h-[45vh]" src={getImageUrl(videoPath)} onLoadedMetadata={onLoaded} onTimeUpdate={onTime} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end mt-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Character</label>
            <select data-testid="capture-char-select" value={charId} onChange={e => setCharId(e.target.value)} className="ff-input w-full text-xs py-1.5 mt-1">
              <option value="">Choose…</option>
              {sceneChars.map(c => <option key={c.id} value={c.id}>{c.name}{c.kling_voice_id ? ' (replaces current native voice)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">Start (s)</label>
            <div className="flex gap-1 mt-1">
              <input data-testid="capture-start" type="number" step="0.1" min="0" value={start} onChange={e => setStart(Number(e.target.value))} className="ff-input w-20 text-xs py-1.5" />
              <button data-testid="capture-mark-start" onClick={() => setStart(r2(videoRef.current?.currentTime || 0))} className="text-[10px] px-2 rounded-lg border border-white/10 text-[#8A8A93] hover:text-white" title="Use current playhead">Mark</button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8A8A93]">End (s)</label>
            <div className="flex gap-1 mt-1">
              <input data-testid="capture-end" type="number" step="0.1" min="0" value={end} onChange={e => setEnd(Number(e.target.value))} className="ff-input w-20 text-xs py-1.5" />
              <button data-testid="capture-mark-end" onClick={() => setEnd(r2(videoRef.current?.currentTime || 0))} className="text-[10px] px-2 rounded-lg border border-white/10 text-[#8A8A93] hover:text-white" title="Use current playhead">Mark</button>
            </div>
          </div>
          <button data-testid="capture-preview-segment" onClick={previewSegment} className="text-[10px] px-2.5 py-2 rounded-lg border border-white/10 text-[#8A8A93] hover:text-white flex items-center gap-1"><Play className="w-3 h-3" /> Play range</button>
        </div>
        <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
          <p data-testid="capture-length-hint" className={`text-[11px] ${valid ? 'text-[#10B981]' : 'text-amber-400'}`}>
            {length ? `${r2(length)}s selected` : 'No range selected'}{!valid && charId && length ? ' — needs 5–30s' : ''}{!charId ? ' — pick a character' : ''}
          </p>
          <button data-testid="capture-voice-submit" onClick={capture} disabled={!valid || busy}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-[#E879F9]/15 text-[#E879F9] hover:bg-[#E879F9]/25 border border-[#E879F9]/30 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clapperboard className="w-3.5 h-3.5" />} {busy ? 'Cloning with Kling…' : 'Capture & lock native voice'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Distinct speaking characters in dialogue order, split by whether they have a Kling native voice.
// `preferred` (scene.native_voice_speakers) lets the user pick which 2 keep their native voice.
export const getNativeSpeakers = (dialogueLines = [], characters = [], preferred = []) => {
  const seen = new Set();
  const withVoice = [], withoutVoice = [];
  for (const line of dialogueLines || []) {
    if (!line?.text?.trim() || !line.character_id || seen.has(line.character_id)) continue;
    const c = characters.find(x => x.id === line.character_id);
    if (!c) continue;
    seen.add(c.id);
    (c.kling_voice_id ? withVoice : withoutVoice).push(c);
  }
  const pref = (preferred || []).map(id => withVoice.find(c => c.id === id)).filter(Boolean);
  const ordered = [...pref, ...withVoice.filter(c => !pref.includes(c))];
  return { withVoice, withoutVoice, used: ordered.slice(0, KLING_MAX_NATIVE_VOICES), dropped: ordered.slice(KLING_MAX_NATIVE_VOICES) };
};

const names = (list) => list.map(c => c.name).join(', ');

// Play one dialogue line in the character's locked Kling voice (server caches per voice+text)
export const NativeLinePreviewButton = ({ line, characters, testId }) => {
  const [state, setState] = useState('idle');
  const audioRef = useRef(null);
  const char = characters.find(c => c.id === line?.character_id);
  if (!char?.kling_voice_id || !line?.text?.trim()) return null;
  const stop = () => { audioRef.current?.pause(); audioRef.current = null; setState('idle'); };
  const play = async () => {
    if (state === 'playing') return stop();
    setState('loading');
    try {
      const res = await api.post('/kling/preview-line', { voice_id: char.kling_voice_id, text: line.text.trim() }, { timeout: 90000 });
      const audio = new Audio(getImageUrl(res.data.audio_path));
      audio.onended = stop;
      audio.onerror = () => { toast.error('Could not play preview'); stop(); };
      audioRef.current = audio;
      await audio.play();
      setState('playing');
    } catch (err) {
      const d = err.response?.data?.detail || '';
      toast.error(d.startsWith('MISSING_KEY') ? (d.split('|')[1] || 'Add a Kling API key in Team Settings') : d || 'Native voice preview failed');
      setState('idle');
    }
  };
  return (
    <button data-testid={testId} onClick={play} disabled={state === 'loading'}
      title={`Hear this line in ${char.name}'s native voice (${char.kling_voice_name || 'Kling'})`}
      className={`shrink-0 flex items-center gap-1 px-1.5 py-1 rounded-lg text-[9px] font-medium border transition-all disabled:opacity-60 ${state === 'playing' ? 'bg-[#E879F9]/15 text-[#E879F9] border-[#E879F9]/30' : 'bg-white/[0.04] text-[#8A8A93] hover:text-[#E879F9] border-white/[0.06]'}`}>
      {state === 'loading' ? <Loader2 className="w-3 h-3 animate-spin" /> : state === 'playing' ? <span className="w-2.5 h-2.5 block bg-[#E879F9] rounded-sm" /> : <Clapperboard className="w-3 h-3" />}
      Native
    </button>
  );
};

// "Keep these N" picker — appears only when more native-voice speakers than Kling allows are in a scene
export const NativeSpeakerTrim = ({ withVoice, used, onSave, testId = 'native-speaker-trim' }) => {
  const [saving, setSaving] = useState(false);
  const usedIds = used.map(c => c.id);
  const toggle = async (c) => {
    let next;
    if (usedIds.includes(c.id)) next = usedIds.filter(id => id !== c.id);
    else next = [...usedIds.slice(-(KLING_MAX_NATIVE_VOICES - 1)), c.id];
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); }
  };
  return (
    <div data-testid={testId} className="flex items-center gap-1.5 flex-wrap mt-1">
      <span className="text-[10px] text-[#8A8A93]">Keep native voice for:</span>
      {withVoice.map(c => {
        const on = usedIds.includes(c.id);
        return (
          <button key={c.id} data-testid={`native-speaker-pill-${c.id}`} disabled={saving} onClick={() => toggle(c)}
            className={`text-[10px] px-2 py-0.5 rounded-full border transition-all disabled:opacity-50 ${on ? 'bg-[#E879F9]/15 text-[#E879F9] border-[#E879F9]/30' : 'text-[#8A8A93] border-white/10 hover:text-white line-through decoration-white/30'}`}>
            {c.name}
          </button>
        );
      })}
      {saving && <Loader2 className="w-3 h-3 animate-spin text-[#8A8A93]" />}
    </div>
  );
};

export const NativeVoicePicker = ({ char, voices, keyMissing, saving, playing, onSet, onPreview, onCloneClick }) => {
  const current = voices.find(v => v.voice_id === char.kling_voice_id);
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06]" data-testid={`native-voice-block-${char.id}`}>
      <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#E879F9] mb-2 flex items-center gap-1.5">
        <Clapperboard className="w-3 h-3" /> Native Video Voice
        <span className="text-[8px] px-1.5 py-px rounded-full bg-[#E879F9]/10 border border-[#E879F9]/25 tracking-normal normal-case font-medium" title="Native voices are baked into the video by Kling 3.0. Veo and Seedance cannot use them.">Kling only</span>
      </label>
      {keyMissing ? (
        <p data-testid={`native-voice-helper-${char.id}`} className="text-[10px] text-[#8A8A93] leading-relaxed">
          {typeof keyMissing === 'string' ? keyMissing : 'Add a Kling API key in Team Settings to lock a native voice for this character.'}
          {' '}<a href="/settings" className="text-[#FF4500] hover:underline">Open Team Settings →</a>
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Select value={char.kling_voice_id || 'none'} disabled={saving}
              onValueChange={(val) => { if (val === 'none') onSet('', ''); else { const v = voices.find(x => x.voice_id === val); onSet(val, v?.voice_name || ''); } }}>
              <SelectTrigger data-testid={`native-voice-select-${char.id}`} className="flex-1 h-9 text-xs bg-[#121216] border-white/10">
                <SelectValue placeholder="No native voice" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A20] border-white/10 max-h-72">
                <SelectItem value="none" className="text-xs text-[#8A8A93]">No native voice</SelectItem>
                {char.kling_voice_id && !current && <SelectItem value={char.kling_voice_id} className="text-xs">{char.kling_voice_name || 'Assigned voice'}</SelectItem>}
                {voices.map(v => (
                  <SelectItem key={v.voice_id} value={v.voice_id} data-testid={`native-voice-option-${v.voice_id}`} className="text-xs">
                    {v.voice_name}<span className="text-[#55555C] ml-1">· {v.type === 'custom' ? 'cloned' : 'preset'}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button data-testid={`native-voice-preview-${char.id}`} disabled={!current?.trial_url} title="Play voice preview"
              onClick={() => onPreview(char.kling_voice_id, current?.trial_url)}
              className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center bg-[#E879F9]/10 text-[#E879F9] hover:bg-[#E879F9]/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              {playing === char.kling_voice_id ? <div className="w-3 h-3 rounded-sm bg-[#E879F9]" /> : <Play className="w-3.5 h-3.5" />}
            </button>
          </div>
          <button data-testid={`native-clone-voice-${char.id}`} onClick={onCloneClick}
            className="mt-2 text-[10px] text-[#00F0FF] hover:text-[#33F3FF] flex items-center gap-1 transition-colors">
            <Upload className="w-3 h-3" /> Clone a native voice from audio…
          </button>
          <p className="mt-1.5 text-[10px] text-[#55555C] leading-relaxed">Used automatically in every scene this character speaks in — when generating with Kling 3.0.</p>
        </>
      )}
    </div>
  );
};

// Shown next to the dialogue editor
export const NativeVoiceDialogueNotice = ({ dialogueLines, characters, preferred, onSavePreferred }) => {
  const { withVoice, used, dropped } = getNativeSpeakers(dialogueLines, characters, preferred);
  if (withVoice.length === 0) return null;
  return (
    <div data-testid="native-voice-dialogue-notice" className="mt-3 space-y-1.5">
      <p className="text-[10px] text-[#8A8A93] flex items-start gap-1.5">
        <Clapperboard className="w-3 h-3 mt-px text-[#E879F9] shrink-0" />
        <span>Native voices locked (Kling 3.0 only): <span className="text-[#E879F9]">{used.map(c => `${c.name} → ${c.kling_voice_name || 'voice'}`).join(' · ')}</span></span>
      </p>
      {dropped.length > 0 && (
        <>
          <p data-testid="native-voice-limit-warning" className="text-[10px] text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
            <span>Kling supports max {KLING_MAX_NATIVE_VOICES} native voices per clip. {withVoice.length} characters with native voices speak here — <b>{names(dropped)}</b> will get a generic AI voice.{onSavePreferred ? ` Pick which ${KLING_MAX_NATIVE_VOICES} to keep:` : ''}</span>
          </p>
          {onSavePreferred && <NativeSpeakerTrim withVoice={withVoice} used={used} onSave={onSavePreferred} />}
        </>
      )}
    </div>
  );
};

// Shown in the Generate Video controls
export const NativeVoiceVideoNotice = ({ dialogueLines, characters, provider, onSwitchToKling, preferred, onSavePreferred }) => {
  const { withVoice, used, dropped } = getNativeSpeakers(dialogueLines, characters, preferred);
  if (withVoice.length === 0) return null;
  if (provider !== 'kling') {
    return (
      <div data-testid="native-voice-provider-warning" className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.06] p-2.5 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-px shrink-0" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed flex-1">
          Native voices for <b>{names(withVoice)}</b> won't apply with {provider === 'veo' ? 'Veo 3.1' : 'Seedance 2.5'} — it will pick its own generic voices.
          {onSwitchToKling && <button data-testid="switch-to-kling-btn" onClick={onSwitchToKling} className="ml-1.5 underline underline-offset-2 text-amber-300 hover:text-white">Switch to Kling 3.0</button>}
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-1.5">
      <div data-testid="native-voice-chip" className="inline-flex items-center gap-1.5 flex-wrap text-[10px] px-2 py-1 rounded-full bg-[#E879F9]/10 text-[#E879F9] border border-[#E879F9]/20">
        <Volume2 className="w-3 h-3" /> Native voices: {used.map(c => `${c.name} (${c.kling_voice_name || 'voice'})`).join(', ')}
      </div>
      {dropped.length > 0 && (
        <>
          <p data-testid="native-voice-limit-warning-video" className="text-[10px] text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
            <span>Max {KLING_MAX_NATIVE_VOICES} native voices per Kling clip — <b>{names(dropped)}</b> will get a generic voice in this video.</span>
          </p>
          {onSavePreferred && <NativeSpeakerTrim withVoice={withVoice} used={used} onSave={onSavePreferred} testId="native-speaker-trim-video" />}
        </>
      )}
    </div>
  );
};
